import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, AlertTriangle } from "lucide-react";
import KitchenScan from "../components/KitchenScan";
import StaplesPicker from "../components/StaplesPicker";
import BarcodeScanner from "../components/BarcodeScanner";
import FridgeNudge from "../components/FridgeNudge";
import ScanHistory from "../components/ScanHistory";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import { SpeechBubble } from "../components/MascotBubble";

export default function Kitchen() {
  const { activeId, activeHousehold, loadHouseholds } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(activeHousehold?.inventory || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [useBy, setUseBy] = useState("");
  const meta = activeHousehold?.inventory_meta || {};
  const daysLeft = (name) => {
    const m = meta[name.toLowerCase()];
    if (!m?.use_by) return null;
    return Math.round((new Date(m.use_by) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  };
  const openEdit = (name) => { setEditing(name); setUseBy(meta[name.toLowerCase()]?.use_by || ""); };
  const saveDate = async () => {
    try {
      await api.put("/kitchen/item", { household_id: activeId, name: editing, use_by: useBy || null });
      await loadHouseholds();
      toast.success(useBy ? `Use-by set for ${editing}` : `Cleared use-by for ${editing}`);
      setEditing(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const payloadFrom = (inv) => ({ ...activeHousehold, currency: activeHousehold.currency || "A$", inventory: inv });

  const persist = async (inv) => {
    setItems(inv);
    setSaving(true);
    try { await api.put(`/households/${activeId}`, payloadFrom(inv)); await loadHouseholds(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const add = () => { const v = input.trim(); if (!v) return; persist([...items, v]); setInput(""); };
  const remove = (i) => persist(items.filter((_, x) => x !== i));

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FBEFE7] pb-28">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate("/millie")} data-testid="kitchen-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">My kitchen</h1>
          <span className="w-11" />
        </div>

        <div className="px-6 mt-4">
          <SpeechBubble testId="kitchen-millie-bubble">Snap a photo of your fridge, freezer or pantry and I'll add what I spot — you just confirm. 📸</SpeechBubble>
        </div>

        <div className="px-6 mt-4">
          <KitchenScan activeId={activeId} onAdded={async (inv) => { setItems(inv); await loadHouseholds(); }} />
        </div>
        <div className="px-6 mt-4"><FridgeNudge activeId={activeId} compact /></div>
        <ScanHistory activeId={activeId} />

        <div className="px-6 mt-6">
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="kitchen-input"
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder="Add an item you have…"
              className="flex-1 bg-white border-2 border-[#FDECEF] rounded-2xl px-4 py-3 font-medium outline-none focus:border-sorted-rose" />
            <button onClick={add} data-testid="kitchen-add" className="w-12 h-12 rounded-full bg-sorted-rose text-white flex items-center justify-center shrink-0"><Plus size={20} strokeWidth={2.5} /></button>
            <BarcodeScanner testPrefix="kitchen-barcode" onFound={async (name, useBy) => {
              const next = items.some((i) => i.toLowerCase() === name.toLowerCase()) ? items : [...items, name];
              await persist(next);
              if (useBy) { try { await api.put("/kitchen/item", { household_id: activeId, name, use_by: useBy }); await loadHouseholds(); } catch { /* ignore */ } }
            }} />
          </div>
          <details className="mt-3" data-testid="kitchen-staples">
            <summary className="text-xs font-bold text-sorted-rose cursor-pointer">Quick-add pantry staples</summary>
            <StaplesPicker value={items} onChange={(v) => persist(v)} testPrefix="kitchen-staples" />
          </details>

          <div className="flex items-center justify-between mt-5 mb-2">
            <h2 className="font-heading text-lg font-semibold text-sorted-ink">In your kitchen ({items.length})</h2>
            {saving && <span className="text-xs text-sorted-slate font-semibold">saving…</span>}
          </div>

          {items.length === 0 ? (
            <p className="text-sorted-slate text-sm">Nothing here yet — add a few staples and I'll plan around them.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {items.map((it, i) => {
                const d = daysLeft(it);
                const urgent = d !== null && d <= 2;
                return (
                  <span key={i} data-testid={`kitchen-item-${i}`} className={`bg-white border-2 ${urgent ? "border-amber-300" : "border-[#FDECEF]"} text-sorted-ink text-sm font-bold pl-3 pr-2 py-2 rounded-full flex items-center gap-2 shadow-soft`}>
                    {urgent && <AlertTriangle size={13} strokeWidth={2.5} className="text-amber-500" />}
                    <button onClick={() => openEdit(it)} data-testid={`kitchen-item-edit-${i}`} className="flex items-center gap-1.5">
                      {it}
                      {d !== null && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d <= 0 ? "bg-sorted-rose text-white" : "bg-muted text-sorted-slate"}`}>{d <= 0 ? "today" : `${d}d`}</span>}
                    </button>
                    <button onClick={() => remove(i)} aria-label={`Remove ${it}`} className="text-sorted-slate"><X size={14} strokeWidth={3} /></button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)}
            className="fixed inset-0 z-50 bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" data-testid="useby-sheet">
            <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6">
              <div className="flex items-center gap-2 text-sorted-ink"><CalendarClock size={18} strokeWidth={2.5} className="text-sorted-rose" /><p className="font-hand text-2xl">When should {editing} be used by?</p></div>
              <p className="text-xs text-sorted-slate mt-1">Leave it blank and Millie will guess for fresh stuff like mince, salad and milk.</p>
              <input type="date" value={useBy} onChange={(e) => setUseBy(e.target.value)} data-testid="useby-input"
                className="mt-4 w-full bg-white border-2 border-[#FDECEF] rounded-2xl px-4 py-3 font-medium text-sorted-ink outline-none focus:border-sorted-rose" />
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button onClick={() => { setUseBy(""); }} data-testid="useby-clear" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full text-sm">Clear</button>
                <button onClick={saveDate} data-testid="useby-save" className="bg-sorted-rose text-white font-bold py-3 rounded-full text-sm">Save</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <BottomNav />
    </div>
  );
}
